/**
 * The markets Jeff is allowed to have an opinion about.
 *
 * ── Why this is a fixed list and not a free field ──────────────────────────
 * `asset` used to be rejected outright by `POST /api/calls`, because anything a
 * client can put in it, a stranger with curl can put in it — and an arbitrary
 * string lands on a permalink under this domain, unfurls with the real OG card,
 * and stays in the global record. "BTC: LONG — institutional accumulation
 * confirmed" is a forged trading signal wearing the site's own branding.
 *
 * A closed vocabulary keeps that shut while letting the field exist. The worst a
 * forger can now do is pick which real ticker their call names, which is exactly
 * what pressing the button does anyway. `price` and `context` stay rejected: this
 * list is the only thing that opened.
 *
 * ── Why the client picks it rather than the server ─────────────────────────
 * The asset has to be on screen when Jeff is released — "JEFF IS CONSIDERING
 * DOGE" is the setup, and a call is not written to the database until the walk
 * ends about a minute later. So the client draws it and the Worker checks it is
 * on this list.
 *
 * ── Why these tickers ──────────────────────────────────────────────────────
 * Every one resolves against Coinbase's keyless public spot endpoint, because a
 * call Jeff cannot be marked right or wrong about is a joke with no punchline.
 * Verified at time of writing; anything delisted later resolves VOID rather than
 * breaking, which is the honest outcome.
 */

export const ASSETS = [
  'BTC',
  'ETH',
  'SOL',
  'XRP',
  'DOGE',
  'ADA',
  'LTC',
  'LINK',
  'AVAX',
  'DOT',
  'MATIC',
  'UNI',
  'AAVE',
  'ATOM',
  'ALGO',
  'PEPE',
  'SHIB',
  'BONK',
  'WIF',
] as const;

export type Asset = (typeof ASSETS)[number];

/** Case-insensitive, because an ID pasted from a chat window arrives lowercased. */
export function isValidAsset(value: unknown): value is Asset {
  return typeof value === 'string' && (ASSETS as readonly string[]).includes(value.toUpperCase());
}

/** Canonical form, or null if it is not a market Jeff trades. */
export function normaliseAsset(value: unknown): Asset | null {
  if (typeof value !== 'string') return null;
  const upper = value.trim().toUpperCase();
  return (ASSETS as readonly string[]).includes(upper) ? (upper as Asset) : null;
}
